/**
 * Place figures read from tax returns and P&Ls into the calculator's year
 * columns. Pure, so the whole document-to-estimate path can be tested.
 *
 * A figure goes to the column its document names: the period in the page
 * header, else a year in the file name. The current year is year-to-date.
 * Cells the owner has already typed are never overwritten.
 */

import type { Candidate, FinancialField } from '../workspace/schema.js';

export type YearRow =
  | 'revenue' | 'cogs' | 'operatingExpenses' | 'netIncome' | 'ownerSalary' | 'depreciation' | 'interest' | 'oneTimeItems';
export type Grid = Record<string, Partial<Record<YearRow, string>>>;

const FIELD_TO_ROW: Partial<Record<FinancialField, YearRow>> = {
  annualRevenue: 'revenue', cogs: 'cogs', operatingExpenses: 'operatingExpenses', netIncome: 'netIncome',
  ownerSalary: 'ownerSalary', depreciation: 'depreciation', interestExpense: 'interest',
};

/** Columns are tax years, oldest first, then 'YTD'. */
export function columnFor(period: string | undefined, fileName: string, cols: string[], thisYear: number): string | null {
  const year = period ?? fileName.match(/\b(20\d{2})\b/)?.[1];
  if (period === 'YTD' || /\bytd\b|year[\s-]*to[\s-]*date/i.test(fileName) || year === String(thisYear)) return 'YTD';
  if (year && cols.includes(year)) return year;
  return year ? null : cols.filter((c) => c !== 'YTD').at(-1) ?? null;
}

export function candidatesToGrid(
  grid: Grid, candidates: Candidate[], fileName: string, cols: string[], thisYear: number,
): { grid: Grid; filled: number } {
  const out: Grid = structuredClone(grid);
  let filled = 0;
  for (const c of candidates) {
    const row = FIELD_TO_ROW[c.field];
    const col = row && columnFor(c.period, fileName, cols, thisYear);
    if (!row || !col) continue;
    const cells = (out[col] ??= {});
    if (cells[row] === undefined || cells[row] === '') { cells[row] = String(c.value); filled++; }
  }
  return { grid: out, filled };
}
